import { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ChevronDownIcon, ClipboardDocumentListIcon, ClipboardIcon } from '@heroicons/react/24/solid';
import { DocumentMagnifyingGlassIcon, FolderArrowDownIcon, PrinterIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ViewAppraisal({ id }) {
  const [appraisals, setAppraisals] = useState([]);
  const [probations, setProbations] = useState([]);
  const [activeMenu, setActiveMenu] = useState('appraisal');
  const [activeAppraisalIndex, setActiveAppraisalIndex] = useState(0);
  const [activeProbationIndex, setActiveProbationIndex] = useState(0);
  const [viewLetter, setLetterView] = useState(false);
  const appraisalRef = useRef(null);
  const probationRef = useRef(null);
  // Fix: define currentAppraisal safely
  const currentAppraisal = appraisals && appraisals.length > 0 ? appraisals[activeAppraisalIndex] : null;


  console.log('Appraisal ID in ViewAppraisal:', id);

  // Tabs config
  const tabs = [
    {
      name: 'Appraisal',
      href: '#',
      icon: ClipboardDocumentListIcon,
      current: activeMenu === 'appraisal',
      onClick: () => setActiveMenu('appraisal'),
    },
    {
      name: 'Probation',
      href: '#',
      icon: ClipboardIcon,
      current: activeMenu === 'probation',
      onClick: () => setActiveMenu('probation'),
    },
  ];

useEffect(() => {

  const fetchData = async () => {
    try {
      const { id: appraisalId } = id;

      console.log('Fetching data for appraisal ID:', appraisalId);

      const response = await fetch(`https://api.insightos.co.uk/api/viewappraisal/${appraisalId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProbations(data.probation || []);
      setAppraisals(data.data || []);

      console.log('Fetched appraisal data:', data.data);
      console.log('Fetched probation data:', data.probation);

    } catch (error) {
      console.error('Error fetching appraisal data:', error);
      setAppraisals([]);
      setProbations([]);
    }
  };

  fetchData();
}, [id]);



  // Print handlers
  const handlePrintAppraisal = useReactToPrint({
    content: () => appraisalRef.current,
  });
  const handlePrintProbation = useReactToPrint({
    content: () => probationRef.current,
  });

  // PDF handlers
  const handleDownloadPDF = (ref, filename) => {
    const input = ref.current;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let yPosition = 0;
      let currentPage = 0;
      const pageCanvasHeight = (pageHeight - 20) * (canvas.height / imgHeight);
      while (yPosition < canvas.height) {
        const pageCanvas = document.createElement('canvas');
        const context = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pageCanvasHeight, canvas.height - yPosition);
        context.drawImage(canvas, 0, yPosition, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
        const pageImgData = pageCanvas.toDataURL('image/png');
        if (currentPage !== 0) pdf.addPage();
        pdf.addImage(pageImgData, 'PNG', 10, 10, imgWidth, (imgWidth * pageCanvas.height) / canvas.width);
        yPosition += pageCanvasHeight;
        currentPage++;
      }
      pdf.save(filename);
    });
  };

  // Tab select handler for mobile
  const handleTabSelect = (e) => {
    const selected = e.target.value;
    setActiveMenu(selected.toLowerCase());
  };

  return (
    <div>
      {/* Tabs */}
      <div>
        <div className="grid grid-cols-1 sm:hidden">
          <select
            value={tabs.find((tab) => tab.current).name}
            onChange={handleTabSelect}
            aria-label="Select a tab"
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          >
            {tabs.map((tab) => (
              <option key={tab.name}>{tab.name}</option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
          />
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  type="button"
                  key={tab.name}
                  aria-current={tab.current ? 'page' : undefined}
                  onClick={tab.onClick}
                  className={classNames(
                    tab.current
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium'
                  )}
                >
                  <tab.icon
                    aria-hidden="true"
                    className={classNames(
                      tab.current ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                      '-ml-0.5 mr-2 size-5'
                    )}
                  />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Appraisal Section */}
      {activeMenu === 'appraisal' && (
        <div>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center mt-4">
            <button onClick={handlePrintAppraisal}
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mb-2 sm:mb-0"
            >
              <PrinterIcon aria-hidden="true" className="-ml-0.5 size-5" />
              Print
            </button>
            <button
              className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-500 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => handleDownloadPDF(appraisalRef, 'appraisal.pdf')}
            >
              <FolderArrowDownIcon aria-hidden="true" className="-ml-0.5 size-5" />
              Download as PDF
            </button>
          </div>
          <div className="mb-4">
            <label className="mr-2 text-sm font-medium">Select Appraisal:</label>
            <select
              value={activeAppraisalIndex}
              onChange={e => setActiveAppraisalIndex(Number(e.target.value))}
              className="w-full rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600"
            >
              {appraisals.map((appraisal, idx) => (
                <option key={idx} value={idx}>
                  {appraisal.ReviewPeriodFrom ? format(new Date(appraisal.ReviewPeriodFrom), 'MMM dd, yyyy') : 'N/A'}
                </option>
              ))}
            </select>
          </div>
          {appraisals.length === 0 || !currentAppraisal ? (
            <div className="p-4 bg-red-600 text-white rounded-lg w-full text-center">No Appraisals For This User</div>
          ) : (
            <div ref={appraisalRef} className="border p-4 rounded-lg bg-white shadow-md max-w-full overflow-x-auto">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Appraisal Information</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 mb-4">
                Details about the employee's performance and feedback.
              </p>
              <dl className="divide-y divide-gray-100">
                {[['Staff Name', currentAppraisal.StaffName],
                  ['Review Period From', currentAppraisal.ReviewPeriodFrom ? format(new Date(currentAppraisal.ReviewPeriodFrom), 'MMM dd, yyyy') : 'N/A'],
                  ['Review Period To', currentAppraisal.ReviewPeriodTo ? format(new Date(currentAppraisal.ReviewPeriodTo), 'MMM dd, yyyy') : 'N/A'],
                  ['Overall Performance', currentAppraisal.OverallPerformance],
                  ['Key Strengths', currentAppraisal.KeyStrengths],
                  ['Areas for Improvement', currentAppraisal.AreasForImprovement],
                  ['Performance Goals Met', currentAppraisal.PerformanceGoalsMet],
                  ['Examples Exceeding Expectations', currentAppraisal.ExamplesExceedingExpectations],
                  ['Deadlines Targets Met', currentAppraisal.DeadlinesTargetsMet],
                  ['Leadership and Teamwork Skills', currentAppraisal.LeadershipTeamworkSkills],
                  ['Feedback from Peers', currentAppraisal.FeedbackFromPeers],
                  ['Development Goals', currentAppraisal.DevelopmentGoals],
                  ['Recommended Training Courses', currentAppraisal.RecommendedTrainingCourses],
                  ['AI Training Report', currentAppraisal.AITrainingReport],
                  ['Skills for Career Advancement', currentAppraisal.SkillsForCareerAdvancement],
                  ['Compensation and Benefits Updates', currentAppraisal.CompensationBenefitsUpdates],
                  ['Overall Summary', currentAppraisal.OverallSummary],
                  ['Role Responsibility Changes', currentAppraisal.RoleResponsibilityChanges],
                  ['Action Plan', currentAppraisal.ActionPlan],
                  ['Employee Comments', currentAppraisal.EmployeeComments],
                  ['Author', currentAppraisal.Author],
                ].map(([label, value], idx) => (
                  <div key={idx} className="px-2 py-3 flex flex-col sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm font-medium text-gray-900">{label}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 break-words">{value || 'N/A'}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Probation Section */}
      {activeMenu === 'probation' && (
        <div>
          <div className="mb-4 w-full flex flex-col gap-2 sm:flex-row sm:items-center mt-4">
            <button onClick={handlePrintProbation}
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full sm:w-auto"
            >
              <PrinterIcon aria-hidden="true" className="-ml-0.5 size-5" />
              Print
            </button>
            <button
              className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-500 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full sm:w-auto"
              onClick={() => handleDownloadPDF(probationRef, 'probation.pdf')}
            >
              <FolderArrowDownIcon aria-hidden="true" className="-ml-0.5 size-5" />
              Download as PDF
            </button>
            <button
              className="inline-flex items-center gap-x-1.5 rounded-md bg-violet-500 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full sm:w-auto"
              onClick={() => setLetterView(true)}
            >
              <DocumentMagnifyingGlassIcon aria-hidden="true" className="-ml-0.5 size-5" />
              View Letter
            </button>
          </div>
          
          <div className="mb-4">
            <label className="mr-2 text-sm font-medium">Select Probation:</label>
            <select
              value={activeProbationIndex}
              onChange={e => setActiveProbationIndex(Number(e.target.value))}
              className="w-full rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600"
            >
              {probations.map((prob, idx) => (
                <option key={idx} value={idx}>
                  {prob.Name || 'N/A'}
                </option>
              ))}
            </select>
          </div>
          {/* Letter Print/Download Section */}

          {viewLetter && probations[activeProbationIndex]?.Letter && (
            <div className="mb-4 border rounded-lg bg-white shadow-lg p-4 sm:p-6 fixed inset-0 z-50 flex flex-col items-center justify-center overflow-y-auto">
              <div className="flex flex-col sm:flex-row justify-between items-center w-full mb-2 gap-2">
                <span className="font-semibold text-lg mr-2">Letter</span>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-700"
                  onClick={() => setLetterView(false)}
                >
                  Close
                </button>
              </div>
              <div className="mb-4 border p-2 sm:p-4 bg-gray-50 rounded-lg overflow-x-auto w-full" style={{maxHeight: '60vh'}}>
                <div dangerouslySetInnerHTML={{ __html: probations[activeProbationIndex].Letter }} />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full justify-center">
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition w-full sm:w-auto"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(probations[activeProbationIndex].Letter);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                  }}
                >
                  <PrinterIcon aria-hidden="true" className="-ml-0.5 size-5" />
                  Print Letter
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition w-full sm:w-auto"
                  onClick={() => {
                    const container = document.createElement('div');
                    container.innerHTML = probations[activeProbationIndex].Letter;
                    document.body.appendChild(container);
                    html2canvas(container, { scale: 2 }).then((canvas) => {
                      const imgData = canvas.toDataURL('image/png');
                      const pdf = new jsPDF('p', 'mm', 'a4');
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      const imgWidth = pageWidth - 20;
                      const imgHeight = (canvas.height * imgWidth) / canvas.width;
                      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
                      pdf.save('probation_letter.pdf');
                      document.body.removeChild(container);
                    });
                  }}
                >
                  <FolderArrowDownIcon aria-hidden="true" className="-ml-0.5 size-5" />
                  Download Letter as PDF
                </button>
              </div>
            </div>
          )}
          {probations.length === 0 ? (
            <div className="p-4 bg-red-600 text-white rounded-lg w-full">No Probation Records For This User</div>
          ) : (
            <div ref={probationRef} className="border p-4 bg-white">
              <h3 className="text-base font-semibold text-gray-900">Probation Information</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Details about the employee's probation period.</p>
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Name</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.Name || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Status</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.Status || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Comments</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.Comments || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Strengths</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.Strengths || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Development Areas</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.DevelopmentAreas || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Extension Reason</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.ExtensionReason || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Areas To Improve</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.AreasToImprove || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Extended End Date</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {probations[activeProbationIndex]?.ExtendedEndDate
                      ? format(new Date(probations[activeProbationIndex].ExtendedEndDate), 'MMM dd, yyyy')
                      : 'N/A'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Support Provided</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.SupportProvided || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Next Review Date</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {probations[activeProbationIndex]?.NextReviewDate
                      ? format(new Date(probations[activeProbationIndex].NextReviewDate), 'MMM dd, yyyy')
                      : 'N/A'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Termination Reason</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.TerminationReason || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Evidence</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.Evidence || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Feedback Given</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.FeedbackGiven || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Final Review Date</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {probations[activeProbationIndex]?.FinalReviewDate
                      ? format(new Date(probations[activeProbationIndex].FinalReviewDate), 'MMM dd, yyyy')
                      : 'N/A'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">HR Signoff</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.HrSignoff || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Manager Name</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{probations[activeProbationIndex]?.ManagerName || 'N/A'}</dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium text-gray-900">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {probations[activeProbationIndex]?.CreatedAt
                      ? format(new Date(probations[activeProbationIndex].CreatedAt), 'MMM dd, yyyy')
                      : 'N/A'}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
